import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { useUser } from '../../features/auth/useUser';
import { Button } from '../../components/common/Button';
import { CreateButton } from '../../components/common/CreateButton';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { BackButton } from '../../components/common/BackButton';
import { Label } from '../../components/common/Label';
import type { LabelVariant } from '../../components/common/Label';
import { apiClient, OAUTH_BINDING_API_BASE } from '../../services/apiClient';
import { NewConnectionModal } from '../connections/NewConnectionModal';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import '../products/ProductsPageHeader.css';
import './AmazonAuthPage.css';

interface AmazonAdsAccount {
  id: string;
  profile_id: string;
  profile_name: string;
  country_code: string;
  account_type: string;
  marketplace_id: string;
  is_active: boolean;
  last_sync_at: string;
}

export default function AmazonAdsAuthPage() {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState('JP');
  const [accounts, setAccounts] = useState<AmazonAdsAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasShownAlert = useRef(false);

  // OAuth成功/失敗メッセージを表示
  useEffect(() => {
    const success = searchParams.get('success');
    const errorMsg = searchParams.get('error');
    const profileCount = searchParams.get('profiles');
    const pending = searchParams.get('pending');

    // URLパラメータがない、または既にアラートを表示済みの場合はスキップ
    if (!success && !errorMsg) return;
    if (hasShownAlert.current) return;

    hasShownAlert.current = true;

    if (success === 'true' && pending === 'true') {
      toast.success(
        'Amazon認証に成功しました。広告プロフィールの取得には Amazon Advertising API のアクセス承認が必要です。承認後にもう一度接続してください。',
        10000,
      );
      // URLパラメータをクリア
      navigate('/connections/amazon-ads', { replace: true });
      fetchAccounts();
    } else if (success === 'true') {
      toast.success(`Amazon広告アカウントの連携に成功しました（${profileCount}プロフィール）`);
      // URLパラメータをクリア
      navigate('/connections/amazon-ads', { replace: true });
      fetchAccounts();
    } else if (errorMsg) {
      toast.error(`Amazon広告アカウントの連携に失敗しました: ${errorMsg}`);
      navigate('/connections/amazon-ads', { replace: true });
    }
  }, [searchParams, navigate, toast]);

  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/amazon-ads/accounts');
      setAccounts(response.data.accounts || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch advertising accounts:', err);
      setError('広告アカウントの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  /**
   * OAuth 開始用の使い捨てチケットを取得して遷移する。
   *
   * 2026-07-29 監査 M-8: 以前はここで `/auth/token` のセッション JWT を
   * そのままクエリに載せてトップレベル遷移していた。JWT が Cloud Run の
   * requestUrl / Vercel のプロキシログ / ブラウザ履歴 / Sentry に平文で残り、
   * 1 件拾われれば有効期限内はそのユーザーとして全 API を叩けた。
   * チケットは単発・短命・セッション Cookie と結合しているため、
   * ログに残っても再利用できない。
   *
   * 2026-08-18: ticket XHR は必ず同一オリジン /api 経由にする
   * (baseURL: OAUTH_BINDING_API_BASE)。既定の VITE_BACKEND_ORIGIN (run.app)
   * 直指しだと、ticket を保持する Flask session Cookie が run.app 側に
   * 置かれ (またはクロスサイト XHR のため保存されず)、続くトップレベル遷移
   * (同一オリジン /api/amazon-ads/auth/start) に session が届かないので
   * ticket 検証で必ず失敗する。start / callback / ticket の 3 リクエストが
   * すべてアプリ自身のドメインを通ることで、state・redirect_uri を含む
   * session が first-party Cookie として一貫する。
   */
  const startAdsOauth = async (marketplace: string) => {
    const ticketResponse = await apiClient.post('/amazon-ads/auth/ticket', undefined, {
      baseURL: OAUTH_BINDING_API_BASE,
    });
    const { ticket } = ticketResponse.data;
    if (!ticket) {
      throw new Error('ticket missing');
    }
    window.location.href =
      `/api/amazon-ads/auth/start?marketplace=${encodeURIComponent(marketplace)}` +
      `&ticket=${encodeURIComponent(ticket)}`;
  };

  const handleConnect = async () => {
    try {
      await startAdsOauth(selectedMarketplace);
    } catch (err) {
      console.error('Failed to initiate Amazon Ads auth:', err);
      toast.error('Amazon広告API認証の開始に失敗しました。再度ログインしてください。');
    }
  };

  const handleReconnect = async (account: AmazonAdsAccount) => {
    try {
      await startAdsOauth(account.country_code);
    } catch (err) {
      console.error('Failed to reconnect Amazon Ads account:', err);
      toast.error('Amazon広告API再認証の開始に失敗しました。');
    }
  };

  const handleDisconnect = async (account: AmazonAdsAccount) => {
    const ok = await confirm({
      title: `Amazon広告アカウント (${account.profile_name || account.profile_id}) の接続を解除しますか？`,
      confirmLabel: '解除する',
      variant: 'danger',
    });
    if (!ok) return;

    try {
      await apiClient.delete(`/amazon-ads/accounts/${account.id}`);
      toast.success('アカウントの接続を解除しました');
      fetchAccounts();
    } catch (err) {
      console.error('Failed to delete Amazon Ads account:', err);
      toast.error('アカウントの削除に失敗しました');
    }
  };

  const getMarketplaceName = (code: string): string => {
    const names: Record<string, string> = {
      JP: '日本',
      US: 'アメリカ',
      GB: 'イギリス',
      DE: 'ドイツ',
      FR: 'フランス',
      IT: 'イタリア',
      ES: 'スペイン',
      CA: 'カナダ',
      AU: 'オーストラリア',
    };
    return names[code] || code;
  };

  const getAccountTypeLabel = (type: string): { text: string; variant: LabelVariant } => {
    switch (type) {
      case 'seller':
        return { text: 'セラー', variant: 'blue' };
      case 'vendor':
        return { text: 'ベンダー', variant: 'purple' };
      case 'agency':
        return { text: 'エージェンシー', variant: 'orange' };
      case 'pending':
        return { text: 'API承認待ち', variant: 'yellow' };
      default:
        return { text: type, variant: 'gray' };
    }
  };

  const getStatusLabel = (is_active: boolean): { text: string; variant: LabelVariant } => {
    return is_active
      ? { text: '有効', variant: 'green' }
      : { text: '無効', variant: 'red' };
  };

  return (
    <div className="page admin-page">
      <div className="header-actions">
        <BackButton to="/connections" />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">Amazon広告API連携</h1>
          <p className="page-description">
            Amazon Advertising API（スポンサーディスプレイ広告など）と連携して、生成した素材を直接広告クリエイティブとして入稿できます。
          </p>
        </div>
      </header>

      {!isAdmin && (
        <div className="readonly-banner" role="status">
          <span className="readonly-banner-icon">
            <Eye size={18} aria-hidden="true" />
          </span>
          <span>閲覧のみ可能です。連携・解除等の操作には管理者権限が必要です。</span>
        </div>
      )}

      {/* 接続済みアカウント一覧 */}
      <section className="accounts-section">
        <div className="accounts-section-head">
          <h2 className="section-title">接続済みアカウント</h2>
          {isAdmin && (
            <CreateButton onClick={() => setIsModalOpen(true)}>新規接続</CreateButton>
          )}
        </div>

        {isLoading && <div className="admin-loading">読み込み中...</div>}

        {error && (
          <div className="admin-error">
            広告アカウントの取得に失敗しました: {error}
          </div>
        )}

        {!isLoading && !error && accounts.length === 0 && (
          <div className="admin-empty-state">
            <div className="admin-empty-state-title">接続済みのAmazon広告アカウントはありません</div>
            <div className="admin-empty-state-description">
              まずは上部の「接続」ボタンからアカウントを接続してください。
            </div>
          </div>
        )}

        {!isLoading && accounts.length > 0 && (
          <div className="table-container">
            <table className="common-table">
              <thead>
                <tr>
                  <th className="col-profile-id">プロフィールID</th>
                  <th className="col-profile-name">プロフィール名</th>
                  <th className="col-country">マーケットプレイス</th>
                  <th className="col-account-type">アカウントタイプ</th>
                  <th className="col-status">ステータス</th>
                  <th className="col-sync-at">接続日時</th>
                  {isAdmin && <th className="col-actions">操作</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const accountType = getAccountTypeLabel(account.account_type);
                  const status = getStatusLabel(account.is_active);

                  return (
                    <tr key={account.id}>
                      <td className="col-profile-id">
                        <code>{account.profile_id}</code>
                      </td>
                      <td className="col-profile-name">{account.profile_name || '-'}</td>
                      <td className="col-country">
                        {getMarketplaceName(account.country_code)} ({account.country_code})
                      </td>
                      <td className="col-account-type">
                        <Label variant={accountType.variant}>{accountType.text}</Label>
                      </td>
                      <td className="col-status">
                        <Label variant={status.variant}>{status.text}</Label>
                      </td>
                      <td className="col-sync-at">
                        {account.last_sync_at
                          ? new Date(account.last_sync_at).toLocaleString('ja-JP')
                          : '-'}
                      </td>
                      {isAdmin && (
                        <td className="col-actions">
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              onClick={() => handleReconnect(account)}
                              variant="primary"
                              size="sm"
                            >
                              更新
                            </Button>
                            <Button
                              onClick={() => void handleDisconnect(account)}
                              variant="danger"
                              size="sm"
                            >
                              接続解除
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <NewConnectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Amazon 広告アカウントに新規接続"
        description="連携する Amazon マーケットプレイスを選択して接続を開始します。Amazon の同意画面に遷移します。"
      >
        <div className="connect-form">
          <div className="form-group">
            <label htmlFor="marketplace">マーケットプレイス</label>
            <select
              id="marketplace"
              className="form-select"
              value={selectedMarketplace}
              onChange={(e) => setSelectedMarketplace(e.target.value)}
            >
              <option value="JP">日本 (amazon.co.jp)</option>
              <option value="US">アメリカ (amazon.com)</option>
              <option value="GB">イギリス (amazon.co.uk)</option>
              <option value="DE">ドイツ (amazon.de)</option>
              <option value="FR">フランス (amazon.fr)</option>
              <option value="IT">イタリア (amazon.it)</option>
              <option value="ES">スペイン (amazon.es)</option>
              <option value="CA">カナダ (amazon.ca)</option>
              <option value="AU">オーストラリア (amazon.com.au)</option>
            </select>
          </div>

          <Button onClick={handleConnect} variant="primary" size="md">
            接続
          </Button>
        </div>
      </NewConnectionModal>
      {dialog}
    </div>
  );
}
